import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ClientEnv } from 'constants/Chat.constants';

import { projectInit } from 'actions';
import type { TurnMetadata } from 'api/resources/conversation/types';
import type { ChatMessage } from 'api/types';

export type AgentResponseV2 = {
  response: string;
  userInput: string;
  metadata?: TurnMetadata;
};

export type ChatStatus =
  | 'idle'
  | 'preparing'
  | 'starting'
  | 'ready'
  | 'sending'
  | 'waitingSlow'
  | 'ended'
  | 'error';

export interface EnvChatState {
  conversationId: string | null;
  messages: ChatMessage[];
  agentResponses: AgentResponseV2[];
  status: ChatStatus;
  requestId: number;
  messageDraft: string;
  endTurn: ChatMessage | null;
  variantName: string;
  channel: string;
  languageCode: string | null;
  asrLanguageCode: string | null;
  ttsLanguageCode: string | null;
}

export interface WebChatState {
  draft: EnvChatState;
  sandbox: EnvChatState;
  'pre-release': EnvChatState;
  live: EnvChatState;
  draftArtifactVersions: {
    artifact?: string;
    lambdaDeployment?: string;
  };
}

export const NO_VARIANT_NAME_WEB = 'No variant';
const DEFAULT_CHANNEL = 'webchat.polyai';

export const initialEnvWebChatState: EnvChatState = {
  conversationId: null,
  messages: [],
  agentResponses: [],
  status: 'idle',
  requestId: 0,
  messageDraft: '',
  endTurn: null,
  variantName: NO_VARIANT_NAME_WEB,
  channel: DEFAULT_CHANNEL,
  languageCode: null,
  asrLanguageCode: null,
  ttsLanguageCode: null,
};

export const initialWebChatState: WebChatState = {
  draft: { ...initialEnvWebChatState },
  sandbox: { ...initialEnvWebChatState },
  'pre-release': { ...initialEnvWebChatState },
  live: { ...initialEnvWebChatState },
  draftArtifactVersions: {},
};

function resetConversation(envState: EnvChatState): EnvChatState {
  return {
    ...envState,
    conversationId: null,
    messages: [],
    agentResponses: [],
    status: 'idle',
    requestId: envState.requestId + 1,
    messageDraft: '',
    endTurn: null,
    asrLanguageCode: null,
    ttsLanguageCode: null,
  };
}

type EnvPayload = { clientEnv: ClientEnv };
type RequestIdPayload = { requestId: number } & EnvPayload;

const webChatSlice = createSlice({
  name: 'webChat',
  initialState: initialWebChatState,
  reducers: {
    webBeginPreparing: (
      state,
      { payload: { clientEnv } }: PayloadAction<EnvPayload>,
    ) => {
      if (state[clientEnv].status !== 'idle') return;
      state[clientEnv].status = 'preparing';
      state[clientEnv].requestId += 1;
    },

    webBeginStarting: (
      state,
      { payload: { clientEnv } }: PayloadAction<EnvPayload>,
    ) => {
      const env = state[clientEnv];
      if (env.status !== 'idle' && env.status !== 'preparing') return;
      if (env.status === 'idle') {
        env.requestId += 1;
      }
      env.status = 'starting';
    },

    webChatStarted: (
      state,
      {
        payload: {
          clientEnv,
          requestId,
          conversationId,
          agentMessage,
          agentResponse,
          asrLanguageCode,
          ttsLanguageCode,
        },
      }: PayloadAction<
        RequestIdPayload & {
          conversationId: string;
          agentMessage: ChatMessage;
          agentResponse: AgentResponseV2;
          asrLanguageCode?: string;
          ttsLanguageCode?: string;
        }
      >,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      if (state[clientEnv].status !== 'starting') return;
      const env = state[clientEnv];
      env.status = 'ready';
      env.conversationId = conversationId;
      if (asrLanguageCode) env.asrLanguageCode = asrLanguageCode;
      if (ttsLanguageCode) env.ttsLanguageCode = ttsLanguageCode;
      env.messages.push(agentMessage);
      env.agentResponses.push(agentResponse);
    },

    webBeginSending: (
      state,
      {
        payload: { clientEnv, message },
      }: PayloadAction<{ message: ChatMessage } & EnvPayload>,
    ) => {
      if (state[clientEnv].status !== 'ready') return;
      state[clientEnv].messages.push(message);
      state[clientEnv].messageDraft = '';
      state[clientEnv].status = 'sending';
      state[clientEnv].requestId += 1;
    },

    webSetWaitingSlow: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      if (state[clientEnv].status !== 'sending') return;
      state[clientEnv].status = 'waitingSlow';
    },

    webMessageSent: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      const { status } = state[clientEnv];
      if (status !== 'sending' && status !== 'waitingSlow') return;
      state[clientEnv].status = 'ready';
    },

    webChatEnded: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      state[clientEnv].status = 'ended';
    },

    webChatErrored: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      const { status } = state[clientEnv];
      if (
        status !== 'preparing' &&
        status !== 'starting' &&
        status !== 'sending' &&
        status !== 'waitingSlow'
      ) {
        return;
      }
      state[clientEnv].status = 'error';
    },

    webResponseReceived: (
      state,
      {
        payload: {
          clientEnv,
          requestId,
          conversationId,
          agentMessage,
          previousMessageMetadata,
          agentResponse,
          conversationEnded,
          asrLanguageCode,
          ttsLanguageCode,
        },
      }: PayloadAction<
        RequestIdPayload & {
          conversationId: string;
          agentMessage: ChatMessage;
          previousMessageMetadata: TurnMetadata;
          agentResponse: AgentResponseV2;
          conversationEnded?: boolean;
          asrLanguageCode?: string;
          ttsLanguageCode?: string;
        }
      >,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      if (state[clientEnv].conversationId !== conversationId) return;
      const { status } = state[clientEnv];
      if (status !== 'sending' && status !== 'waitingSlow') return;
      const env = state[clientEnv];
      if (asrLanguageCode) env.asrLanguageCode = asrLanguageCode;
      if (ttsLanguageCode) env.ttsLanguageCode = ttsLanguageCode;
      const previousMessage = env.messages.at(-1);
      if (previousMessage) {
        previousMessage.metadata = previousMessageMetadata;
      }
      env.messages.push(agentMessage);
      env.agentResponses.push(agentResponse);
      if (conversationEnded) env.status = 'ended';
    },

    setWebChatMessageDraft: (
      state,
      {
        payload: { clientEnv, message },
      }: PayloadAction<{ message: string } & EnvPayload>,
    ) => {
      state[clientEnv].messageDraft = message;
    },

    setWebEndTurns: (
      state,
      {
        payload: { clientEnv, message, metadata },
      }: PayloadAction<
        { message: string; metadata: TurnMetadata } & EnvPayload
      >,
    ) => {
      state[clientEnv].endTurn = {
        text: message,
        from: 'agent',
        metadata,
      };
    },

    setWebChatVariantName: (
      state,
      {
        payload: { clientEnv, variantName },
      }: PayloadAction<{ variantName: string } & EnvPayload>,
    ) => {
      if (state[clientEnv].variantName === variantName) return;
      state[clientEnv] = {
        ...resetConversation(state[clientEnv]),
        variantName,
      };
    },

    setWebChatLanguageCode: (
      state,
      {
        payload: { clientEnv, languageCode },
      }: PayloadAction<{ languageCode: string } & EnvPayload>,
    ) => {
      if (state[clientEnv].languageCode === languageCode) return;
      state[clientEnv] = {
        ...resetConversation(state[clientEnv]),
        languageCode,
      };
    },

    resetWebChat: (
      state,
      { payload }: PayloadAction<{ clientEnv?: ClientEnv } | undefined>,
    ) => {
      if (!payload?.clientEnv) {
        return { ...initialWebChatState };
      }
      state[payload.clientEnv] = resetConversation(state[payload.clientEnv]);
    },

    setWebDraftArtifactVersions: (
      state,
      {
        payload: { artifactVersion, lambdaDeployment },
      }: PayloadAction<{
        artifactVersion: string;
        lambdaDeployment?: string;
      }>,
    ) => {
      state.draftArtifactVersions = {
        artifact: artifactVersion,
        lambdaDeployment,
      };
    },

    resetWebDraftArtifactVersions: state => {
      state.draftArtifactVersions = {};
    },
  },
  extraReducers: builder => {
    builder.addCase(projectInit, () => ({ ...initialWebChatState }));
  },
});

export const {
  webBeginPreparing,
  webBeginStarting,
  webChatStarted,
  webBeginSending,
  webSetWaitingSlow,
  webMessageSent,
  webChatEnded,
  webChatErrored,
  webResponseReceived,
  setWebChatMessageDraft,
  setWebEndTurns,
  setWebChatVariantName,
  setWebChatLanguageCode,
  resetWebChat,
  setWebDraftArtifactVersions,
  resetWebDraftArtifactVersions,
} = webChatSlice.actions;

export default webChatSlice.reducer;
